import React, { useEffect, useRef } from 'react';
import { skillCards } from '../mockData';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import VanillaTilt from 'vanilla-tilt';

const SkillsSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const cardRefs = useRef([]);

  useEffect(() => {
    cardRefs.current.forEach((card) => {
      if (card) {
        VanillaTilt.init(card, {
          max: 5,
          speed: 400,
          glare: true,
          'max-glare': 0.2
        });
      }
    });

    return () => {
      cardRefs.current.forEach((card) => {
        if (card && card.vanillaTilt) {
          card.vanillaTilt.destroy();
        }
      });
    };
  }, []);

  return (
    <section id="skills" className="min-h-screen flex items-center justify-center py-20 relative" ref={ref}>
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Skills & <span className="text-cyan-400">Technologies</span>
          </h2>
          <p className="text-gray-400 text-lg mb-4">
            Beginner-friendly, honest skills focused on Cloud & DevOps fundamentals
          </p>
          {/* Accent line */}
          <div className="flex justify-center">
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full"></div>
          </div>
        </motion.div>

        {/* Cards Grid */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skillCards.map((card, index) => (
            <motion.div
              key={card.id}
              ref={(el) => (cardRefs.current[index] = el)}
              className={`bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6 hover:border-cyan-400/50 transition-all duration-300 glow-on-hover ${
                index >= 3 ? 'lg:col-span-1' : ''
              }`}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              style={{
                gridColumn: index === 3 ? 'span 1 / span 1' : undefined
              }}
            >
              {/* Card Title */}
              <h3 className="text-xl font-bold text-cyan-400 mb-4 uppercase tracking-wide">
                {card.title}
              </h3>

              {/* Skills List */}
              <div className="space-y-4">
                {card.skills.map((skill, skillIndex) => (
                  <div key={skillIndex} className="space-y-1">
                    <h4 className="text-white font-semibold text-base">
                      {skill.name}
                    </h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {skill.description}
                    </p>
                    {skill.note && (
                      <p className="text-cyan-400 text-xs italic">
                        {skill.note}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;
